<template>
  <MainLayout>
    <template #header>
      <h1 class="text-2xl font-bold mb-4">Exchange App Organisations</h1>
    </template>

    <div class="flex flex-wrap items-center gap-3 mb-4">
      <Button
        label="Add Organisation"
        icon="fa-solid fa-building"
        class="bg-blue-900 hover:bg-blue-800 text-white border-blue-900 py-3 px-6"
        @click="showAddOrganisationDialog = true"
      />
      <span class="p-input-icon-left flex-1 min-w-[260px] max-w-md">
        <i class="pi pi-search" />
        <InputText
          v-model="search"
          placeholder="Search by name or subdomain"
          class="w-full"
        />
      </span>
      <span class="text-sm text-gray-500">
        {{ filteredOrganisations.length }} / {{ organisations.length }}
      </span>
    </div>

    <DataTable
      :value="filteredOrganisations"
      :paginator="true"
      :rows="100"
      :rowsPerPageOptions="[25, 50, 100, 200]"
      dataKey="id"
      responsiveLayout="scroll"
      class="p-datatable-organisations"
      :loading="loading"
      @rowClick="onRowClick"
    >
      <template #empty>
        <div class="text-center">
          {{ organisations.length ? 'No organisations match your search' : 'No organisations found' }}
        </div>
      </template>
      <template #loading>
        <div class="text-center">Loading organisations...</div>
      </template>

      <!-- Organisation Name -->
      <Column field="name" header="Organisation Name" :sortable="true">
        <template #body="slotProps">
          <router-link
            :to="{ name: 'OrganisationDetail', params: { id: slotProps.data.id } }"
            class="text-blue-600 hover:underline font-semibold"
            @click.stop
          >
            {{ slotProps.data.name }}
          </router-link>
        </template>
      </Column>

      <!-- Subdomain -->
      <Column field="subdomain" header="Subdomain" :sortable="true">
        <template #body="slotProps">
          <a
            v-if="slotProps.data.subdomain || slotProps.data.custom_login_subdomain"
            :href="`https://${slotProps.data.subdomain || slotProps.data.custom_login_subdomain}.alldoq.com`"
            target="_blank"
            rel="noopener noreferrer"
            class="font-mono text-sm text-blue-600 hover:underline inline-flex items-center"
            @click.stop
          >
            {{ slotProps.data.subdomain || slotProps.data.custom_login_subdomain }}
            <i class="pi pi-external-link ml-1 text-xs"></i>
          </a>
          <span v-else class="font-mono text-sm">-</span>
        </template>
      </Column>

      <!-- Created Date -->
      <Column field="inserted_at" header="Created" :sortable="true">
        <template #body="slotProps">
          {{ formatDate(slotProps.data.inserted_at) }}
        </template>
      </Column>

    </DataTable>

    <!-- Add Organisation Dialog -->
    <Dialog
      v-model:visible="showAddOrganisationDialog"
      modal
      header="Add Organisation"
      :style="{ width: '600px' }"
      :closable="true"
      :dismissableMask="false"
      class="add-organisation-dialog"
    >
      <div class="p-fluid p-4">
        <div class="field mb-4">
          <label for="org-name" class="block mb-2 font-medium text-gray-700">Organisation Name *</label>
          <InputText id="org-name" v-model="newOrganisation.name" class="w-full" placeholder="ACME Corporation" />
        </div>

        <div class="field mb-4">
          <label for="org-subdomain" class="block mb-2 font-medium text-gray-700">Custom Login Subdomain</label>
          <InputText id="org-subdomain" v-model="newOrganisation.custom_login_subdomain" class="w-full" placeholder="acme-corp" />
          <small class="text-gray-500 mt-1 block">
            Lowercase letters, numbers, and hyphens only (3-63 characters).
            A DNS A record will be created on GoDaddy automatically.
          </small>
        </div>

        <div class="field mb-4 flex items-center justify-between border rounded p-3">
          <span class="font-medium text-gray-700">Brand with Logo</span>
          <InputSwitch v-model="newOrganisation.brand_with_logo" />
        </div>

        <div class="mt-6 mb-4">
          <h3 class="text-lg font-semibold mb-3">Organisation Settings</h3>

          <div class="field mb-3 flex items-center justify-between border rounded p-3">
            <span class="font-medium text-gray-700">Allow External Invites</span>
            <InputSwitch v-model="newOrganisation.settings.allow_external_invites" />
          </div>

          <div class="field mb-3">
            <label for="archive-days" class="block mb-2 font-medium text-gray-700">Archive Days</label>
            <InputNumber id="archive-days" v-model="newOrganisation.settings.archive_days" :min="0" class="w-full" />
            <small class="text-gray-500 mt-1 block">Number of days before cases are automatically archived (0 = never)</small>
          </div>
        </div>

        <div class="mt-6 mb-4">
          <h3 class="text-lg font-semibold mb-3">Feature Flags</h3>
          <p class="text-sm text-gray-500 mb-3">
            Enable or disable product features for this environment. Disabled features are
            greyed out in the destination UI.
          </p>
          <div
            v-for="flag in featureFlagDefs"
            :key="flag.key"
            class="field mb-2 flex items-center justify-between border rounded p-3"
          >
            <span class="font-medium text-gray-700">{{ flag.label }}</span>
            <InputSwitch
              :modelValue="getFlag(flag)"
              @update:modelValue="(val) => setFlag(flag, val)"
            />
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3 p-3">
          <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text py-3 px-6"
            @click="showAddOrganisationDialog = false"
          />
          <Button
            label="Create Organisation"
            icon="pi pi-check"
            class="bg-blue-900 hover:bg-blue-800 text-white border-blue-900 py-3 px-6"
            @click="createOrganisation"
            :loading="creatingOrganisation"
          />
        </div>
      </template>
    </Dialog>
  </MainLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useAuthStore } from '../store/auth';
import MainLayout from '../components/MainLayout.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import InputSwitch from 'primevue/inputswitch';
import Button from 'primevue/button';
import axios from 'axios';

const router = useRouter();
const toast = useToast();
const authStore = useAuthStore();

// Data
const organisations = ref([]);
const loading = ref(true);

// Filtering (client-side, matches the Feature flags + Associates sections)
const search = ref('');

const filteredOrganisations = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return organisations.value;
  return organisations.value.filter((org) => {
    const name = (org.name || '').toLowerCase();
    const sub = (org.subdomain || org.custom_login_subdomain || '').toLowerCase();
    return name.includes(term) || sub.includes(term);
  });
});

// Dialog states
const showAddOrganisationDialog = ref(false);
const creatingOrganisation = ref(false);

// Feature flags shown in the create dialog. Settings-scoped flags
// (case_workflows_enabled, contacts_enabled) are nested under settings
// in the alldoq Organisation schema.
const SETTINGS_FLAGS = ['case_workflows_enabled', 'contacts_enabled'];
const featureFlagDefs = [
  { key: 'worklist_enabled',            label: 'Worklist' },
  { key: 'doq_builder_enabled',         label: 'DoQ Builder' },
  { key: 'time_tracking_enabled',       label: 'Time Tracking' },
  { key: 'invoicing_enabled',           label: 'Invoicing' },
  { key: 'inline_pdf_viewing',          label: 'Inline PDF Viewing' },
  { key: 'inline_radiology_viewing',    label: 'Inline Radiology Viewing' },
  { key: 'disable_document_conversion', label: 'Disable Document Conversion' },
  { key: 'hide_associates_tab',         label: 'Hide Associates Tab' },
  { key: 'case_workflows_enabled',      label: 'Case Workflows' },
  { key: 'contacts_enabled',            label: 'Contacts' }
];

const blankOrganisation = () => ({
  name: '',
  custom_login_subdomain: '',
  brand_with_logo: false,
  worklist_enabled: false,
  doq_builder_enabled: false,
  time_tracking_enabled: false,
  invoicing_enabled: false,
  inline_pdf_viewing: false,
  inline_radiology_viewing: false,
  disable_document_conversion: false,
  hide_associates_tab: false,
  settings: {
    allow_external_invites: false,
    archive_days: 0,
    case_workflows_enabled: false,
    contacts_enabled: false
  }
});

const newOrganisation = ref(blankOrganisation());

const getFlag = (flag) => {
  const target = SETTINGS_FLAGS.includes(flag.key)
    ? newOrganisation.value.settings
    : newOrganisation.value;
  return !!target[flag.key];
};

const setFlag = (flag, val) => {
  if (SETTINGS_FLAGS.includes(flag.key)) {
    newOrganisation.value.settings[flag.key] = val;
  } else {
    newOrganisation.value[flag.key] = val;
  }
};

// Methods
const loadOrganisations = async () => {
  loading.value = true;
  try {
    const response = await axios.get('/api/v1/organisations');
    organisations.value = response.data.data || [];
  } catch (error) {
    console.error('Error loading organisations:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load organisations',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
};

const onRowClick = (event) => {
  const rowData = event.data;
  router.push({
    name: 'OrganisationDetail',
    params: { id: rowData.id }
  });
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const createOrganisation = async () => {
  creatingOrganisation.value = true;
  try {
    const response = await axios.post(
      '/api/v1/organisations',
      { organisation: newOrganisation.value }
    );

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Organisation created successfully',
      life: 3000
    });

    showAddOrganisationDialog.value = false;
    const dns = response.data?.dns;
    if (dns && dns.success === false && !dns.skipped) {
      toast.add({
        severity: 'warn',
        summary: 'DNS warning',
        detail: `Organisation created but DNS A record failed: ${dns.error || 'unknown error'}`,
        life: 6000
      });
    }
    newOrganisation.value = blankOrganisation();
    await loadOrganisations();
  } catch (error) {
    console.error('Error creating organisation:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.error || 'Failed to create organisation',
      life: 3000
    });
  } finally {
    creatingOrganisation.value = false;
  }
};

// Lifecycle
onMounted(async () => {
  if (authStore.isAuthenticated) {
    await loadOrganisations();
  } else {
    router.push({ name: 'Login' });
  }
});

// Refetch when the exchange instance (core/tula) changes so the toggle takes
// effect without a full page reload.
watch(() => authStore.exchangeInstance, () => {
  loadOrganisations();
});
</script>

<style scoped>
.p-datatable-organisations .p-datatable-tbody > tr > td {
  padding: 0.75rem 1rem;
}

/* Make table rows clickable */
:deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}

:deep(.p-datatable-tbody > tr:hover) {
  background-color: #f8fafc;
}

/* Dialog styling */
:deep(.add-organisation-dialog .p-dialog-header) {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

:deep(.add-organisation-dialog .p-dialog-content) {
  padding: 0;
}

:deep(.add-organisation-dialog .p-dialog-footer) {
  padding: 0;
  border-top: 1px solid #e5e7eb;
}

.p-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.p-inputtext {
  border: 1px solid #ced4da !important;
  border-radius: 4px !important;
  transition: border-color 0.2s, box-shadow 0.2s !important;
  width: 100% !important;
  display: block !important;
}

.p-inputtext:hover {
  border-color: #2563eb !important;
}

.p-inputtext:focus {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.25) !important;
  outline: 0 none !important;
}

.field {
  margin-bottom: 1rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}
</style>
